/**
 * ModularPatientRouteWrapper
 *
 * Wraps every `/patient/:patientId/adult-autism` (and future modular assessment)
 * route. Same architectural pattern as PatientRouteWrapper but consumes
 * ModularPatientDataContext instead of PatientDataContext.
 *
 * Responsibilities:
 * 1. Flushes any in-flight stage writes before fetching (prevents stale reads
 *    from overwriting pending writes — mirrors ADHD PatientRouteWrapper)
 * 2. Triggers fetchPatientData on mount / patientId change
 * 3. Gates child rendering until loadedPatientId matches the URL param
 * 4. Shows a loading skeleton while data is being fetched
 * 5. Guards module identity: a deep link to another module's route (e.g.
 *    /cyp-adhd for an adultAutism patient) redirects to the patient's own
 *    overview instead of rendering the wrong module's UI over whatever the
 *    context resolves
 */
import React, { useEffect, useContext } from 'react';
import { useParams, useLocation, Navigate } from 'react-router-dom';
import { ModularPatientDataContext } from '../context/ModularPatientDataContext';
import ReviewSessionTracker from '../analytics/ReviewSessionTracker';

function ModularPatientLoading({ summary }) {
  const name = summary && (summary.firstName || summary.lastName)
    ? `${summary.firstName || ''} ${summary.lastName || ''}`.trim()
    : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700 mx-auto"></div>
        <p className="mt-4 text-gray-600">
          {name ? `Loading ${name}…` : 'Loading patient…'}
        </p>
      </div>
    </div>
  );
}

export default function ModularPatientRouteWrapper({ children, LoadingComponent }) {
  const { patientId } = useParams();
  const location = useLocation();
  const {
    header,
    loadedPatientId,
    fetchPatientData,
    flushStageWrites,
    error,
  } = useContext(ModularPatientDataContext);

  useEffect(() => {
    if (!patientId) return;
    // Flush any in-flight writes before fetching new patient data to prevent
    // a stale Firestore read from overwriting pending writes. Same pattern as
    // ADHD PatientRouteWrapper: await flushPendingUpdates() before fetch.
    const loadFreshData = async () => {
      await flushStageWrites();
      fetchPatientData(patientId);
    };
    loadFreshData();
  }, [fetchPatientData, flushStageWrites, patientId]);

  if (error) {
    return null; // ModularPatientDataContext handles the redirect
  }

  // Only render children when the loaded data belongs to THIS patient
  const isDataForThisPatient = header && loadedPatientId === patientId;
  if (!isDataForThisPatient) {
    const summary = location.state?.patientSummary;
    if (LoadingComponent) {
      return <LoadingComponent summary={summary} />;
    }
    return <ModularPatientLoading summary={summary} />;
  }

  // Module guard: the URL says which module's UI this route renders; the
  // loaded header says which module the patient actually belongs to. On a
  // mismatch, send the clinician to the patient's real overview (legacy ADHD
  // headers carry no assessmentModule — their overview is /patient/:id).
  const routeModule = /cyp-adhd/.test(location.pathname)
    ? 'cypAdhd'
    : /adult-autism|adult-asd|-autism/.test(location.pathname)
      ? 'adultAutism'
      : null;
  const patientModule = header.assessmentModule || 'adultAdhd';
  if (routeModule && patientModule !== routeModule) {
    const overviewPathByModule = {
      adultAutism: `/patient/${patientId}/adult-autism`,
      cypAdhd: `/patient/${patientId}/cyp-adhd`,
      adultAdhd: `/patient/${patientId}`,
    };
    return (
      <Navigate
        to={overviewPathByModule[patientModule] || `/patient/${patientId}`}
        replace
      />
    );
  }

  // Renders nothing; measures review sittings on the report review routes.
  // After the data-ownership gate for the same reason as PatientRouteWrapper.
  return (
    <>
      {children}
      <ReviewSessionTracker />
    </>
  );
}
